import type { v2 } from '@deque/billing-service-client';
import { type TFunction } from 'i18next';
import React, { type ReactElement, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  hasPlanName,
  PRODUCT_URLS,
  ProductNames,
  ProductSlugs
} from '~/common/constants';
import { useAuthContext } from '~/common/contexts/auth';
import { useEnterprises } from '~/common/contexts/enterprises';
import { useProducts } from '~/common/contexts/products';
import { useServerInfo } from '~/common/contexts/serverInfo';
import PlanName from './PlanName';
import Products from './Products';
import ProductCard from './V2ProductCard';

export interface ActiveProductsProps {
  subscriptions: Array<v2.UserSubscription | v2.EnterpriseSubscription>;
  isAdmin?: boolean;
  enterpriseAdmins?: string[];
  showFooter?: boolean;
  axeAuditorIntegrationUrl: string;
}

// This function gets the user's keycloakId and enterpriseId and sends them as query parameters to the slackbot install link
export const getSlackInstallUrl = (
  billingUser: v2.User | null,
  activeEnterprise: v2.Enterprise | null
) => {
  if (!billingUser || !activeEnterprise) {
    return '';
  }

  // check if client has active, paid subscription to axe-assistant-slack
  const hasValidSubscription = activeEnterprise.subscriptions.some(
    s =>
      s.product_slug === ProductSlugs.axeAssistantSlack &&
      s.purchase_state === 'paid'
  );

  if (!hasValidSubscription) {
    return '';
  }

  const baseUrl = PRODUCT_URLS[ProductSlugs.axeAssistantSlack].install;
  const dequeRegion = window.appConfig.DEPLOY_REGION?.toUpperCase() || 'US';
  const params = new URLSearchParams({
    keycloak_id: billingUser.keycloak_id,
    enterprise_id: activeEnterprise.id,
    deque_region: dequeRegion
  });

  return `${baseUrl}?${params.toString()}`;
};

export interface GetProductResourcesParams {
  t: TFunction;
  getProductBySlug: (slug: ProductSlugs) => v2.Product | undefined;
  subscriptions: Array<v2.UserSubscription | v2.EnterpriseSubscription>;
  isJazzbandEnabled: boolean;
  isSelfProvisioningEnabled?: boolean;
  axeAuditorIntegrationUrl?: string;
  billingUser?: v2.User | null;
  activeEnterprise?: v2.Enterprise | null;
  isAdmin?: boolean;
}

export const getProductResources = ({
  t,
  getProductBySlug,
  subscriptions,
  isJazzbandEnabled,
  isSelfProvisioningEnabled = false,
  axeAuditorIntegrationUrl = '',
  billingUser = null,
  activeEnterprise = null,
  isAdmin = false
}: GetProductResourcesParams) => {
  const monitorSubscription = subscriptions.find(
    sub => sub.product_slug === ProductSlugs.axeMonitor
  ) as v2.EnterpriseSubscription | undefined;
  const reportsSubscription = subscriptions.find(
    sub => sub.product_slug === ProductSlugs.axeReports
  ) as v2.EnterpriseSubscription | undefined;
  const auditorSubscription = subscriptions.find(
    sub => sub.product_slug === ProductSlugs.axeAuditor
  ) as v2.EnterpriseSubscription | undefined;

  return {
    [ProductSlugs.axeDevToolsExtension]: [
      ...(isSelfProvisioningEnabled
        ? [
            {
              name: t(`{{productName}} Plans`, {
                productName: getProductBySlug(ProductSlugs.axeDevToolsExtension)
                  ?.name
              }),
              url: PRODUCT_URLS[ProductSlugs.axeDevToolsExtension].plans
            }
          ]
        : []),
      {
        name: t(`{{productName}} Home`, {
          productName: getProductBySlug(ProductSlugs.axeDevToolsExtension)?.name
        }),
        url: PRODUCT_URLS[ProductSlugs.axeDevToolsExtension].root
      },
      {
        name: t(`{{productName}} Docs`, {
          productName: getProductBySlug(ProductSlugs.axeDevToolsExtension)?.name
        }),
        url: PRODUCT_URLS[ProductSlugs.axeDevToolsExtension].docs,
        openInNewTab: true
      }
    ],
    [ProductSlugs.axeDevToolsMobile]: [
      ...(isJazzbandEnabled
        ? [
            {
              name: t('Mobile Dashboard'),
              url: PRODUCT_URLS[ProductSlugs.axeDevToolsMobile].dashboard,
              openInNewTab: true
            },
            {
              name:
                getProductBySlug(ProductSlugs.axeDevToolsWatcher)?.name ??
                ProductNames.developerHub,
              url: PRODUCT_URLS[ProductSlugs.axeDevToolsWatcher].projects,
              openInNewTab: true
            },
            {
              name: t(`{{productName}} Docs`, {
                productName: getProductBySlug(ProductSlugs.axeDevToolsMobile)
                  ?.name
              }),
              url: PRODUCT_URLS[ProductSlugs.axeDevToolsMobile].docsSDK,
              openInNewTab: true
            }
          ]
        : [
            {
              name: t(`Dashboard`),
              url: PRODUCT_URLS[ProductSlugs.axeDevToolsMobile].dashboard,
              openInNewTab: true
            },
            {
              name: t(`{{productName}} Analyzer Docs`, {
                productName: getProductBySlug(ProductSlugs.axeDevToolsMobile)
                  ?.name
              }),
              url: PRODUCT_URLS[ProductSlugs.axeDevToolsMobile].docsAnalyzer,
              openInNewTab: true
            },
            {
              name: t(`{{productName}} SDKs Docs`, {
                productName: getProductBySlug(ProductSlugs.axeDevToolsMobile)
                  ?.name
              }),
              url: PRODUCT_URLS[ProductSlugs.axeDevToolsMobile].docsSDK,
              openInNewTab: true
            }
          ]),

      {
        name: t(`Sample Android App`),
        url: 'https://github.com/dequelabs/axe-devtools-android-sample-app',
        openInNewTab: true
      },
      {
        name: t(`Sample iOS App`),
        url: 'https://github.com/dequelabs/axe-devtools-ios-sample-app',
        openInNewTab: true
      }
    ],
    [ProductSlugs.axeDevToolsWatcher]: [
      {
        name: t(`{{productName}} Home`, {
          productName: getProductBySlug(ProductSlugs.axeDevToolsWatcher)?.name
        }),
        url: PRODUCT_URLS[ProductSlugs.axeDevToolsWatcher].projects
      },
      {
        name: t(`{{productName}} Quickstart Instructions`, {
          productName: getProductBySlug(ProductSlugs.axeDevToolsWatcher)?.name
        }),
        url: PRODUCT_URLS[ProductSlugs.axeDevToolsWatcher].startup
      },
      {
        name: t(`{{productName}} Full Documentation`, {
          productName: getProductBySlug(ProductSlugs.axeDevToolsWatcher)?.name
        }),
        url: PRODUCT_URLS[ProductSlugs.axeDevToolsWatcher].docs,
        openInNewTab: true
      }
    ],
    [ProductSlugs.axeDevToolsHTML]: [
      {
        name: t(`{{productName}} Docs`, {
          productName: getProductBySlug(ProductSlugs.axeDevToolsHTML)?.name
        }),
        url: 'https://docs.deque.com/devtools-html',
        openInNewTab: true
      },
      {
        name: t(`{{productName}} Home`, {
          productName: getProductBySlug(ProductSlugs.axeDevToolsHTML)?.name
        }),
        url: PRODUCT_URLS[ProductSlugs.axeDevToolsExtension].root
      },
      ...(!isSelfProvisioningEnabled
        ? [
            {
              name: t(`{{productName}} Plans`, {
                productName: getProductBySlug(ProductSlugs.axeDevToolsHTML)
                  ?.name
              }),
              url: PRODUCT_URLS[ProductSlugs.axeDevToolsExtension].plans
            }
          ]
        : []),
      {
        name: t(`{{agora}} Artifact Repository`, {
          agora: 'Agora'
        }),
        url: 'https://agora.dequecloud.com/artifactory/webapp',
        openInNewTab: true
      }
    ],
    [ProductSlugs.axeLinter]: [
      {
        name: t(`{{productName}} Plans`, {
          productName: getProductBySlug(ProductSlugs.axeLinter)?.name
        }),
        url: PRODUCT_URLS[ProductSlugs.axeLinter].plans,
        openInNewTab: true
      },
      {
        name: t(`{{productName}} Home`, {
          productName: getProductBySlug(ProductSlugs.axeLinter)?.name
        }),
        url: PRODUCT_URLS[ProductSlugs.axeLinter].docs,
        openInNewTab: true
      }
    ],
    [ProductSlugs.dequeUniversity]: [
      {
        name: t(`{{productName}} Home`, {
          productName: getProductBySlug(ProductSlugs.dequeUniversity)?.name
        }),
        url: PRODUCT_URLS[ProductSlugs.dequeUniversity].site,
        openInNewTab: true
      }
    ],
    [ProductSlugs.axeSpider]: [
      {
        name: t(`{{productName}} Home`, {
          productName: getProductBySlug(ProductSlugs.axeSpider)?.name
        }),
        url: PRODUCT_URLS[ProductSlugs.axeSpider].root
      }
    ],
    [ProductSlugs.jiraDirectCloudIntegration]: [
      {
        name: t(`Manage Connections and Templates`),
        url: PRODUCT_URLS[ProductSlugs.jiraDirectCloudIntegration]
          .root as string
      },
      {
        name: t(`{{productName}} Docs`, {
          productName: getProductBySlug(ProductSlugs.jiraDirectCloudIntegration)
            ?.name
        }),
        url: PRODUCT_URLS[ProductSlugs.jiraDirectCloudIntegration]
          .docs as string,
        openInNewTab: true
      }
    ],
    [ProductSlugs.jiraOnPremIntegration]: [
      {
        name: t(`Manage Connections and Templates`),
        url: PRODUCT_URLS[ProductSlugs.jiraOnPremIntegration].root as string
      },
      {
        name: t(`{{productName}} Docs`, {
          productName: getProductBySlug(ProductSlugs.jiraOnPremIntegration)
            ?.name
        }),
        url: PRODUCT_URLS[ProductSlugs.jiraOnPremIntegration].docs as string,
        openInNewTab: true
      }
    ],
    [ProductSlugs.axeMonitor]: [
      {
        name: t(`{{productName}} Docs`, {
          productName: getProductBySlug(ProductSlugs.axeMonitor)?.name
        }),
        url: PRODUCT_URLS[ProductSlugs.axeMonitor].docs as string,
        openInNewTab: true
      },
      ...(monitorSubscription && monitorSubscription.instance_url
        ? [
            {
              name: t(`{{productName}} Home`, {
                productName: getProductBySlug(ProductSlugs.axeMonitor)?.name
              }),
              url: monitorSubscription.instance_url,
              openInNewTab: true
            }
          ]
        : [])
    ],
    [ProductSlugs.axeReports]: [
      {
        name: t(`{{productName}} Docs`, {
          productName: getProductBySlug(ProductSlugs.axeReports)?.name
        }),
        url: PRODUCT_URLS[ProductSlugs.axeReports].docs as string,
        openInNewTab: true
      },
      ...(reportsSubscription && reportsSubscription.instance_url
        ? [
            {
              name: t(`{{productName}} Home`, {
                productName: getProductBySlug(ProductSlugs.axeReports)?.name
              }),
              url: reportsSubscription.instance_url,
              openInNewTab: true
            }
          ]
        : [])
    ],
    [ProductSlugs.axeAuditor]: [
      {
        name: t(`{{productName}} Docs`, {
          productName: getProductBySlug(ProductSlugs.axeAuditor)?.name
        }),
        url: PRODUCT_URLS[ProductSlugs.axeAuditor].docs as string,
        openInNewTab: true
      },
      ...(auditorSubscription && auditorSubscription.instance_url
        ? [
            {
              name: t(`{{productName}} Home`, {
                productName: getProductBySlug(ProductSlugs.axeAuditor)?.name
              }),
              url: auditorSubscription.instance_url,
              openInNewTab: true
            }
          ]
        : [])
    ],
    [ProductSlugs.axeAuditorIntegration]: [
      {
        url: axeAuditorIntegrationUrl,
        openInNewTab: true
      },
      {
        name: t(`{{productName}} Docs`, {
          productName: getProductBySlug(ProductSlugs.axeAuditorIntegration)
            ?.name
        }),
        url: PRODUCT_URLS[ProductSlugs.axeAuditorIntegration].docs as string,
        openInNewTab: true
      }
    ],
    [ProductSlugs.axeAssistantSlack]: [
      ...(isAdmin
        ? [
            {
              name: t(`{{productName}} Install`, {
                productName: getProductBySlug(ProductSlugs.axeAssistantSlack)
                  ?.name
              }),
              url: getSlackInstallUrl(
                billingUser ?? null,
                activeEnterprise ?? null
              ),
              openInNewTab: true
            }
          ]
        : []),
      {
        name: t(`{{productName}} Docs`, {
          productName: getProductBySlug(ProductSlugs.axeAssistantSlack)?.name
        }),
        url: PRODUCT_URLS[ProductSlugs.axeAssistantSlack].docs,
        openInNewTab: true
      },
      {
        name: t(`{{productName}} Info`, {
          productName: getProductBySlug(ProductSlugs.axeAssistantSlack)?.name
        }),
        url: PRODUCT_URLS[ProductSlugs.axeAssistantSlack].info,
        openInNewTab: true
      }
    ],
    [ProductSlugs.axeAssistantTeams]: [
      {
        name: t(`{{productName}} Docs`, {
          productName: getProductBySlug(ProductSlugs.axeAssistantTeams)?.name
        }),
        url: PRODUCT_URLS[ProductSlugs.axeAssistantTeams].docs,
        openInNewTab: true
      },
      {
        name: t(`{{productName}} Info`, {
          productName: getProductBySlug(ProductSlugs.axeAssistantTeams)?.name
        }),
        url: PRODUCT_URLS[ProductSlugs.axeAssistantTeams].info,
        openInNewTab: true
      }
    ],
    [ProductSlugs.customIntegration]: [
      {
        name: t(`Manage Connections`),
        url: PRODUCT_URLS[ProductSlugs.customIntegration].root as string
      },
      {
        name: t(`{{productName}} Docs`, {
          productName: getProductBySlug(ProductSlugs.customIntegration)?.name
        }),
        url: PRODUCT_URLS[ProductSlugs.customIntegration].docs,
        openInNewTab: true
      }
    ]
  };
};

const ActiveProducts = ({
  subscriptions,
  isAdmin = false,
  enterpriseAdmins = [],
  showFooter = false,
  axeAuditorIntegrationUrl
}: ActiveProductsProps): ReactElement => {
  const { t } = useTranslation();
  const { getProductBySlug } = useProducts();
  const { serverInfo, loading: serverInfoLoading } = useServerInfo();

  const dequeUniversitySubscription = subscriptions.find(
    sub => sub.product_slug === ProductSlugs.dequeUniversity
  );
  const activeSubscriptions = subscriptions.filter(
    sub => sub.product_slug !== ProductSlugs.dequeUniversity
  );
  const { billingUser, user } = useAuthContext(); // get billing user info
  const { activeEnterprise } = useEnterprises(); // get active enterprise

  const connectPortalGroup = window.appConfig.CONNECT_PORTAL_GROUP;

  const canAccessConnectPortal =
    connectPortalGroup && user?.groups.includes(connectPortalGroup);

  const resources = useMemo(
    () =>
      getProductResources({
        t,
        getProductBySlug,
        isSelfProvisioningEnabled:
          !serverInfoLoading && serverInfo?.isSelfProvisioningEnabled,
        subscriptions,
        axeAuditorIntegrationUrl,
        billingUser,
        activeEnterprise,
        isAdmin,
        isJazzbandEnabled:
          (!serverInfoLoading && serverInfo?.jazzband.isEnabled) || false
      }),
    [t, getProductBySlug, serverInfo, serverInfoLoading, subscriptions, isAdmin]
  );

  if (
    enterpriseAdmins.length === 0 &&
    activeSubscriptions.length === 0 &&
    !PRODUCT_URLS[ProductSlugs.dequeUniversity].site
  ) {
    // If there are no cards to display, hide the section
    return <></>;
  }

  return (
    <Products
      heading={t('Active Product Subscriptions')}
      showFooter={showFooter}
    >
      {!!enterpriseAdmins.length && (
        <ProductCard title={'Axe ' + t('Account')} admins={enterpriseAdmins} />
      )}
      {activeSubscriptions.map(subscription => (
        <ProductCard
          key={subscription.id}
          title={getProductBySlug(subscription.product_slug)?.name as string}
          planName={
            hasPlanName(subscription.product_slug) && (
              <PlanName subscription={subscription} />
            )
          }
          resources={
            resources[subscription.product_slug as keyof typeof resources]
          }
        />
      ))}
      {/* If in any env Deque university subscription is provided but Deque university site is missing(which shouldn't be a case), still product card will be hidden */}
      {PRODUCT_URLS[ProductSlugs.dequeUniversity].site && (
        <ProductCard
          title={getProductBySlug(ProductSlugs.dequeUniversity)?.name as string}
          planName={
            !!dequeUniversitySubscription && (
              <PlanName subscription={dequeUniversitySubscription} />
            )
          }
          resources={resources[ProductSlugs.dequeUniversity]}
        />
      )}
      {canAccessConnectPortal && (
        <ProductCard
          title={t('Deque Customer Portal')}
          description={t('Manage your service requests')}
          hideProductLogo={true}
          resources={[
            {
              name: t('Deque Customer Portal'),
              url: 'https://connect.deque.com/portal',
              openInNewTab: true
            }
          ]}
        />
      )}
    </Products>
  );
};

export default ActiveProducts;
